import { z } from "zod";
import { CostCategorySchema } from "./cost.js";

// ─── TransactionCategorySchema ─────────────────────────────────────────────────
// Re-exports the canonical 8-value category enum for use in classification logic.
export const TransactionCategorySchema = CostCategorySchema;
export type TransactionCategory = z.infer<typeof TransactionCategorySchema>;

// ─── VatRateSchema ─────────────────────────────────────────────────────────────
// Accepted values for default_vat_rate.
// 'zw' = zwolniony (VAT-exempt).
export const VatRateSchema = z.enum(["23", "8", "5", "0", "zw"]);
export type VatRate = z.infer<typeof VatRateSchema>;

// ─── ClassificationRuleResponseSchema ─────────────────────────────────────────
// Shape of a single rule returned by GET /api/classification-rules.
export const ClassificationRuleResponseSchema = z.object({
  id: z.string().uuid(),
  pattern: z.string(),
  match_mode: z.enum(["EXACT", "CONTAINS"]),
  match_field: z.enum(["counterparty", "description"]),
  category: TransactionCategorySchema,
  cost_name: z.string(),
  default_vat_rate: VatRateSchema,
  priority: z.number().int().min(0).max(999),
  is_active: z.boolean(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type ClassificationRuleResponse = z.infer<typeof ClassificationRuleResponseSchema>;

// ─── CreateClassificationRuleSchema ───────────────────────────────────────────
// Request body for POST /api/classification-rules.
export const CreateClassificationRuleSchema = z.object({
  /** Full legal company name or keyword. Minimum 2 characters. */
  pattern: z.string().trim().min(2, "Pattern must be at least 2 characters"),
  match_mode: z.enum(["EXACT", "CONTAINS"]),
  match_field: z.enum(["counterparty", "description"]),
  category: TransactionCategorySchema,
  /** Human-readable cost label for reporting. */
  cost_name: z.string().trim().min(1, "Cost name is required"),
  default_vat_rate: VatRateSchema,
  /** Higher value = higher precedence when multiple rules match. Range 0–999. */
  priority: z.number().int().min(0).max(999).default(0),
});
export type CreateClassificationRule = z.infer<typeof CreateClassificationRuleSchema>;

// ─── UpdateClassificationRuleSchema ───────────────────────────────────────────
// Request body for PATCH /api/classification-rules/:id. All fields optional.
export const UpdateClassificationRuleSchema = CreateClassificationRuleSchema
  .partial()
  .extend({ is_active: z.boolean().optional() });
export type UpdateClassificationRule = z.infer<typeof UpdateClassificationRuleSchema>;

// ─── RunClassificationRequestSchema ───────────────────────────────────────────
// Request body for POST /api/classifications/run.
// Exactly one of (statement_id) or (from_date + to_date) must be provided.
export const RunClassificationRequestSchema = z.union([
  z.object({
    statement_id: z.string().uuid(),
    overwrite_existing: z.boolean().default(false),
  }),
  z.object({
    from_date: z.string().date(),
    to_date: z.string().date(),
    overwrite_existing: z.boolean().default(false),
  }),
]);
export type RunClassificationRequest = z.infer<typeof RunClassificationRequestSchema>;

// ─── RunClassificationResponseSchema ──────────────────────────────────────────
// Response body for POST /api/classifications/run.
export const RunClassificationResponseSchema = z.object({
  classified_count: z.number().int().min(0),
  ignored_count: z.number().int().min(0),
  do_weryfikacji_count: z.number().int().min(0),
  skipped_count: z.number().int().min(0),
  errors: z.array(z.string()),
});
export type RunClassificationResponse = z.infer<typeof RunClassificationResponseSchema>;

// ─── ManualClassifySchema ──────────────────────────────────────────────────────
// Request body for PATCH /api/costs/:id/classify (reviewer manual resolution).
// DO_WERYFIKACJI is explicitly excluded — a reviewer must assign a real category.
export const ManualClassifySchema = z.object({
  category: TransactionCategorySchema.exclude(["DO_WERYFIKACJI"]),
  cost_name: z.string().trim().min(1, "Cost name is required"),
  default_vat_rate: VatRateSchema,
});
export type ManualClassify = z.infer<typeof ManualClassifySchema>;
